import React, { useState, useEffect } from "react";
import axios from "axios";
import { Table, Checkbox, message, Card, Row, Col, Input, Select } from "antd";
import { API_URL } from "../../url_config";
import {NavigationStepsCds} from "../../components/navbars/navbar_cds"
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";

const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne (zones texte : Commentaire + Aide) ===
function TableGeolocalisation({ record, onChange, setIsUpdating }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handleChange = async (field, value) => {
    setIsUpdating(true);
    onChange(record.key, field, value);

    try {
      await axios.patch(
        `${API_URL}/validations/validation_geolocalisation/${record.id}/`,
        { [field]: value }
      );
    } catch (error) {
      console.error(error);
      message.error("Erreur lors de la mise à jour");
    } finally {
      setTimeout(() => setIsUpdating(false), 4000);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={5}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handleChange("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span>Informations</span>
                <span style={{ fontSize: 12, color: "#888" }}>
                  (Supervision)
                </span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={5}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handleChange("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationGeolocalisationCds = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // === Chargement et rafraîchissement auto des données ===
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isUpdating) return; // ❌ ne recharge pas pendant une mise à jour

      try {
        const res = await axios.get(
          `${API_URL}/validations/validation_geolocalisation/?ot_tag=cds`
        );
        if (!isMounted) return;

        const formatted = res.data.map((item, index) => ({
          key: index,
          id: item.id,
          ligne: item.ligne,
          coquille: item.coquille,
          configuration_ad: item.configuration_ad,
          remontee_de_ad_sae_tr: item.remontee_de_ad_sae_tr,
          geolocalisation: item.geolocalisation,
          gestion_ad: item.gestion_ad,
          remontee_de_ad_tetra: item.remontee_de_ad_tetra,
          ecoute_d_ambiance: item.ecoute_d_ambiance,
          prise_en_compte_appel: item.prise_en_compte_appel,
          comment: item.comment,
          aide: item.aide,
          signal: item.signal,
        }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    setLoading(true);
    fetchData();

    // 🔁 Rechargement automatique toutes les 5 secondes
    const interval = setInterval(fetchData, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isUpdating]);

  // === Gestion de la mise à jour des données ===
  const handleDataChange = async (key, field, value) => {
    setIsUpdating(true);

    // ✅ Mise à jour locale immédiate
    setData((prev) =>
      prev.map((item) => (item.key === key ? { ...item, [field]: value } : item))
    );

    try {
      const updatedItem = data.find((item) => item.key === key);
      if (updatedItem) {
        await axios.patch(
          `${API_URL}/validations/validation_geolocalisation/${updatedItem.id}/`,
          { [field]: value }
        );
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    } finally {
      // ✅ redonne le feu vert après un court délai
      setTimeout(() => setIsUpdating(false), 2000);
    }
  };

  // === Colonnes du tableau ===
  const columns = [
    { title: "Ligne", dataIndex: "ligne" },
    {
      title: "Coquille",
      dataIndex: "coquille",
      render: (val, record) => (
        <Input
          value={val || ""}
          onChange={(e) =>
            handleDataChange(record.key, "coquille", e.target.value)
          }
          placeholder="Saisir une coquille..."
          style={{
            width: "100%",
            borderRadius: "6px",
          }}
        />
      ),
    },

    {
      title: "Configuration AD SAE/V3",
      dataIndex: "configuration_ad",
      render: (val, record) => {
        const getColor = (value) => {
          if (value === "ok") return "#52c41a"; // 🟢 vert clair
          if (value === "ko") return "#ff4d4f"; // 🔴 rouge clair
          return "#000000"; // ⚫ noir
        };

        return (
          <Select
            value={val || ""}
            onChange={(value) =>
              handleDataChange(record.key, "configuration_ad", value)
            }
            style={{
              width: 120,
              color: getColor(val), // ✅ texte coloré
              fontWeight: 600,
            }}
            options={[
              { value: "", label: <span style={{ color: "#000" }}> </span> },
              { value: "ok", label: <span style={{ color: "#52c41a" }}>OK</span> },
              { value: "ko", label: <span style={{ color: "#ff4d4f" }}>KO</span> },
            ]}
            dropdownStyle={{ textAlign: "center" }}
          />
        );
      },
    },


    {
      title: "Remontée de AD SAE-TR",
      dataIndex: "remontee_de_ad_sae_tr",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "remontee_de_ad_sae_tr", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Géolocalisation",
      dataIndex: "geolocalisation",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "geolocalisation", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Acquittement / Clôture",
      dataIndex: "gestion_ad",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "gestion_ad", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Remontée de AD TETRA",
      dataIndex: "remontee_de_ad_tetra",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "remontee_de_ad_tetra", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Écoute d’ambiance",
      dataIndex: "ecoute_d_ambiance",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "ecoute_d_ambiance", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
      title: "Appel individuel",
      dataIndex: "prise_en_compte_appel",
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "prise_en_compte_appel", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
        />
      ),
    },
    {
    title: "ETAT",
    dataIndex: "signal",
    render: (val, record) => {
    // 0 = pas encore choisi, 1 = validé, -1 = non validé
    const color =
      val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
    const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

    const handleClick = () => {
      // Cycle entre 0 → 1 → -1 → 0 ...
      const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
      handleDataChange(record.key, "signal", nextVal);
    };

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            backgroundColor: color,
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "16px",
            userSelect: "none",
            transition: "all 0.3s ease",
          }}
        >
          {symbol}
        </div>
      </div>
    );
    },
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
    <NavigationStepsCds selectedKey="geolocalisation" />
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TableGeolocalisation
              record={record}
              onChange={handleDataChange}
              setIsUpdating={setIsUpdating}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
pagination={false}
      />

      <div style={{ padding: 20 }}>

            <ModifyItemPage
              apiUrl={`${API_URL}/validations/validation_geolocalisation/`}
              ot_tag="cds"
              title="Gestion"
              rowKey="id"
              trigger={<Button icon={<EditOutlined />} shape="circle" />}
              columns={[
                { title: "Ligne", dataIndex: "ligne", key: "ligne" },
                //{ title: "Coquille", dataIndex: "coquille", key: "coquille" },
              ]}
              formFields={[
                { name: "ligne", label: "Ligne", placeholder: "Entrez la ligne" },
                //{ name: "coquille", label: "Coquille", placeholder: "Entrez la coquille" },
              ]}
            />
      </div>


    </div>
  );
};

export default ValidationGeolocalisationCds;
